import { ReviewNotices, ReviewSummary } from "./manuscript-review";
/* eslint-disable @next/next/no-img-element -- Provider returns authenticated object URLs; this component is framework independent. */
import { useState } from "react";
import { Field, Button } from "../../design-system";
import type {
  FileRef,
  LibraryAdapter,
  ManuscriptCheck,
} from "./library-adapter";
import { useLibraryResource, libraryError } from "./library-runtime";
export function useManuscriptCheck(
  provider: LibraryAdapter,
  customerId: string,
  body: string,
  files: FileRef[],
  enabled = true,
) {
  return useLibraryResource(
    provider,
    enabled ? JSON.stringify(["check", customerId, body, files]) : null,
    () => provider.check(customerId, body, files),
  );
}
export function LibraryMedia({
  provider,
  customerId,
  file,
  name,
  video = false,
}: {
  provider: LibraryAdapter;
  customerId: string;
  file: string;
  name: string;
  video?: boolean;
}) {
  const r = useLibraryResource(
    provider,
    JSON.stringify(["file", customerId, file]),
    () => provider.fileUrl(customerId, file),
  );
  const [failed, setFailed] = useState(false);
  if (r.error)
    return (
      <p className="ml-error" role="alert">
        {r.error}
        <Button variant="ghost" onClick={r.retry}>
          다시 읽기
        </Button>
      </p>
    );
  if (!r.data)
    return <div className="ml-media-placeholder">파일 읽는 중 · {name}</div>;
  return (
    <figure className="ml-media-placeholder ml-file-preview">
      {video ? (
        <video src={r.data} controls aria-label={name} />
      ) : (
        <img src={r.data} alt={name} onError={() => setFailed(true)} />
      )}
      <figcaption>{name}</figcaption>
      {failed && <p role="alert">이미지를 표시할 수 없습니다.</p>}
    </figure>
  );
}
export function CheckedPreview({
  provider,
  customerId,
  check,
}: {
  provider: LibraryAdapter;
  customerId: string;
  check: ManuscriptCheck;
}) {
  return (
    <article className="ml-preview">
      <ReviewSummary
        errors={check.errors.length}
        warnings={check.warnings.length}
        notices={check.notices?.length}
      />
      {!!check.notices?.length && (
        <details>
          <summary>검토 {check.notices.length}개 보기</summary>
          <ReviewNotices notices={check.notices} />
        </details>
      )}
      <span className="ml-caption">
        {
          { public: "전체", neighbors: "이웃", private: "비공개" }[
            check.settings.visibility
          ]
        }{" "}
        공개 · {check.settings.category ?? "기본 카테고리"}
      </span>
      <h2>{check.title || "제목 없음"}</h2>
      {check.blocks.map((b, i) =>
        b.type === "media" ? (
          <div key={i}>
            {b.files?.map((f, j) => {
              const m = check.media.find((m) =>
                f.fileId ? m.fileId === f.fileId : m.name === f.name,
              );
              const fileId = f.fileId ?? m?.fileId;
              return fileId ? (
                <LibraryMedia
                  key={j}
                  provider={provider}
                  customerId={customerId}
                  file={fileId}
                  name={f.name}
                  video={m?.video}
                />
              ) : (
                <p key={j}>{f.name} · 첨부 없음</p>
              );
            })}
          </div>
        ) : b.type === "heading" ? (
          <h3 key={i}>{b.text}</h3>
        ) : b.type === "divider" ? (
          <hr key={i} />
        ) : b.type === "quote" ? (
          <blockquote key={i}>{b.text}</blockquote>
        ) : b.type === "link" ? (
          <a
            key={i}
            href={/^https?:\/\//.test(b.url ?? "") ? b.url : undefined}
            target="_blank"
            rel="noreferrer"
          >
            {b.text ?? b.url}
          </a>
        ) : (
          <p key={i}>{b.text}</p>
        ),
      )}
    </article>
  );
}
export function LibraryPreview({
  provider,
  customerId,
  body,
  files,
}: {
  provider: LibraryAdapter;
  customerId: string;
  body: string;
  files: FileRef[];
}) {
  const r = useManuscriptCheck(provider, customerId, body, files);
  return r.error ? (
    <p role="alert" className="ml-error">
      {r.error}
      <Button variant="ghost" onClick={r.retry}>
        다시 검사
      </Button>
    </p>
  ) : r.data ? (
    <CheckedPreview
      provider={provider}
      customerId={customerId}
      check={r.data}
    />
  ) : (
    <p role="status">원고 읽는 중</p>
  );
}
export function LibraryEditor({
  provider,
  customerId,
  value,
  onChange,
  files,
  onFiles,
  onBusyChange,
}: {
  provider: LibraryAdapter;
  customerId: string;
  value: string;
  onChange: (v: string) => void;
  files: FileRef[];
  onFiles: (f: FileRef[]) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const check = useManuscriptCheck(provider, customerId, value, files);
  const upload = async (selected: File[]) => {
    setBusy(true);
    onBusyChange(true);
    setError("");
    const added: FileRef[] = [];
    try {
      for (const file of selected)
        added.push(await provider.uploadFile(customerId, file));
    } catch (e) {
      setError(libraryError(e));
    } finally {
      if (added.length) onFiles([...files, ...added]);
      setBusy(false);
      onBusyChange(false);
    }
  };
  return (
    <div className="lwd-stack">
      <div className="lwd-actions">
        <a href="/meta-crew-manuscript/원고-양식.md" download>
          빈 원고 양식
        </a>
        <a href="/meta-crew-manuscript/원고-작성-가이드.md" download>
          작성 가이드
        </a>
      </div>
      <Field
        label="원고 파일 불러오기"
        type="file"
        accept=".md,.txt"
        disabled={busy}
        onChange={async (e) => {
          try {
            const f = e.target.files?.[0];
            if (f) onChange(await f.text());
          } catch (e) {
            setError(libraryError(e));
          }
        }}
      />
      <label className="field">
        <span>원고</span>
        <textarea
          aria-label="원고"
          className="input lwd-manuscript"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      <Field
        label="사진·영상 첨부"
        type="file"
        multiple
        accept="image/*,video/*"
        disabled={busy}
        onChange={(e) => {
          void upload(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      {!!files.length && (
        <div className="ml-editor-files">
          {files.map((f) => (
            <span key={f.fileId}>
              {f.name}
              <button
                type="button"
                disabled={busy}
                aria-label={f.name + " 첨부 제거"}
                onClick={() =>
                  onFiles(files.filter((x) => x.fileId !== f.fileId))
                }
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {busy && <p role="status">첨부 업로드 중</p>}
      {error && (
        <p role="alert" className="ml-error">
          {error}
        </p>
      )}
      <section className="lwd-preview" aria-label="원고 확인">
        {check.error ? (
          <p role="alert">
            {check.error}
            <Button onClick={check.retry}>다시 검사</Button>
          </p>
        ) : check.data ? (
          <>
            <strong>{check.data.title || "원고 확인"}</strong>
            <p>
              {check.data.valid
                ? "형식 검사를 통과했습니다. 원고함에 새 버전으로 저장할 수 있습니다."
                : "형식 검사 오류"}
            </p>
            {check.data.errors.map((e, i) => (
              <p role="alert" key={i}>
                {e}
              </p>
            ))}
            {check.data.warnings.map((e, i) => (
              <p key={i}>{e}</p>
            ))}
            <details open>
              <summary>본문 순서 미리보기</summary>
              <CheckedPreview
                provider={provider}
                customerId={customerId}
                check={check.data}
              />
            </details>
          </>
        ) : (
          <p>검사 중</p>
        )}
      </section>
    </div>
  );
}
